import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import { AddCircle, CalendarMonth, Delete } from "@mui/icons-material";
import { Button, ButtonBase, Grid, Tooltip } from "@mui/material";
import CircularLoader from "components/CircularLoader";
import MDBox from "components/MDBox";
import MonthSelectionMenu from "components/MonthSelectionMenu";
import { useAppContextController } from "context/AppContext";
import Calendar from "examples/Calendar";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subtractDateRanges } from "utils/helpers/arrays";

const slotIcons = {
  NEW_SLOT: <AddCircle color="white" fontSize="medium" />,
  // EXISTING_SLOT: <Delete color="white" fontSize="medium" />,
  DELETED_SLOT: <Delete color="white" fontSize="medium" />,
  EVENT_SLOT: <CalendarMonth color="white" fontSize="medium" />,
};

const JobAvailabilityCalendar = ({
  slotList,
  setSlotList,
  interviewSlotList,
  bindCalendarApi,
  selectedRecruiter,
  onDatesSet,
  isLoading,
  isReadOnly,
}) => {
  const { company } = useAppContextController();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const calendarRef = useRef(null);
  const calendarApi = calendarRef.current?.getApi();

  useEffect(() => {
    if (bindCalendarApi) bindCalendarApi(calendarApi);
  }, [bindCalendarApi, calendarApi]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
  const setDateToFirst = (date) => date.toISOString().split("T")[0];
  const [startingDate, setStartingDate] = useState(setDateToFirst(new Date()));
  const currentDateCalendar = calendarApi?.getDate();

  const handleClickMonth = (goToDate) => {
    calendarApi.gotoDate(goToDate.toDate());
  };

  const onPressJumpTo = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const removeSlotById = (id) => {
    setSlotList(
      slotList.flatMap((slot) => {
        if (slot.id.toString() !== id.toString()) {
          return [slot];
        }
        if (slot.title === "NEW_SLOT") {
          return [];
        }
        return [
          {
            ...slot,

            title: "DELETED_SLOT",
            className: "error",
            display: "background",
          },
        ];
      })
    );
  };

  const handleSelectSlots = (info) => {
    if (isReadOnly) return;
    if (isLoading) return;
    if (!selectedRecruiter) {
      enqueueSnackbar("No recruiter selected", { variant: "error" });
      return;
    }

    // Your code to handle the selection
    if (info.start && info.end) {
      const startMoment = moment(info.start);
      const endMoment = moment(info.end);

      // Get all contained slots
      const encasedSlots = [];
      for (let index = 0; index < slotList.length; index += 1) {
        const slot = slotList[index];
        const slotStart = moment(slot.start);
        const slotEnd = moment(slot.end);

        if (
          slotEnd.isSameOrAfter(startMoment) &&
          slotStart.isSameOrBefore(endMoment) &&
          slot.title !== "DELETED_SLOT"
        ) {
          // Conflict detected
          encasedSlots.push(slot);
        }
      }

      // Delete all conflicting and adjacent slots, we'll recreate them if necessary

      // Index IDs to delete
      const idsToDeleteIndexed = {};
      for (let index = 0; index < encasedSlots.length; index += 1) {
        const slot = encasedSlots[index];
        idsToDeleteIndexed[slot.id.toString()] = true;
      }

      const newSlots = slotList.flatMap((slot) => {
        if (!idsToDeleteIndexed[slot.id.toString()]) {
          return [slot];
        }

        if (slot.title === "EXISTING_SLOT") {
          return [
            {
              ...slot,
              title: "DELETED_SLOT",
              className: "error",
              display: "background",
            },
          ];
        }

        if (slot.title === "NEW_SLOT") {
          return [];
        }

        return [slot];
      });

      let newStart = startMoment;
      let newEnd = endMoment;

      for (let index = 0; index < encasedSlots.length; index += 1) {
        const slot = encasedSlots[index];
        const slotStart = moment(slot.start);
        const slotEnd = moment(slot.end);

        if (slotStart.isBefore(newStart)) {
          newStart = slotStart;
        }
        if (slotEnd.isAfter(newEnd)) {
          newEnd = slotEnd;
        }
      }

      newSlots.push({
        id: Math.round(Math.random() * 10000),
        title: "NEW_SLOT",
        start: moment(newStart).toISOString(),
        end: moment(newEnd).toISOString(),
        className: "success",
      });

      setSlotList(newSlots);
    }
  };

  const handleClickExistingSlot = (d) => {
    if (isReadOnly) return;
    if (isLoading || d.event.title === "EVENT_SLOT") return;
    const eventId = d.event?.id;
    if (eventId) {
      removeSlotById(eventId);
    }
  };

  const reducedSlots = useMemo(() => {
    const newAvailabilities = subtractDateRanges(
      slotList ?? [],
      interviewSlotList ?? [],
      null,
      "start",
      "end"
    );
    return newAvailabilities;
  }, [interviewSlotList, slotList]);

  const [calendarMinTime, calendarMaxTime] = useMemo(() => {
    const lowerLimit = company?.aiSettings?.interviewAutoSchedulingMinAvailabilityTime;
    const upperLimit = company?.aiSettings?.interviewAutoSchedulingMaxAvailabilityTime;

    let lowerLimitFormatted = "06:00:00";
    let upperLimitFormatted = "21:00:00";

    if (lowerLimit) {
      // eslint-disable-next-line prefer-destructuring
      lowerLimitFormatted = moment(`${new Date().toISOString().split("T")[0]}T${lowerLimit}`)
        .format()
        .split("T")[1]
        .split("-")[0]
        .split(".")[0];
    }
    if (upperLimit) {
      // eslint-disable-next-line prefer-destructuring
      upperLimitFormatted = moment(`${new Date().toISOString().split("T")[0]}T${upperLimit}`)
        .format()
        .split("T")[1]
        .split("-")[0]
        .split(".")[0];
    }

    return [lowerLimitFormatted, upperLimitFormatted];
  }, [
    company?.aiSettings?.interviewAutoSchedulingMaxAvailabilityTime,
    company?.aiSettings?.interviewAutoSchedulingMinAvailabilityTime,
  ]);

  return (
    <>
      <MDBox sx={{ position: "relative" }}>
        <Calendar
          ref={calendarRef}
          headerToolbar={{
            left: "jumpToButton",
            center: "title",
            // right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          initialView="timeGridWeek"
          initialDate={startingDate}
          allDaySlot={false}
          slotMinTime={calendarMinTime}
          slotMaxTime={calendarMaxTime}
          events={[...reducedSlots, ...interviewSlotList]}
          longPressDelay={500}
          // height="calc(70vh - 200px)"
          eventDisplay="block"
          eventContent={(arg) => (
            <Tooltip
              disableInteractive
              placement="top"
              title={arg.event?.extendedProps?.description ?? ""}
            >
              <Grid
                container
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                {arg.event?.extendedProps?.link ? (
                  <ButtonBase onClick={() => navigate(arg.event?.extendedProps?.link)}>
                    {slotIcons[arg.event?.title]}
                  </ButtonBase>
                ) : (
                  <>{slotIcons[arg.event?.title]}</>
                )}
              </Grid>
            </Tooltip>
          )}
          eventClick={handleClickExistingSlot}
          eventStartEditable={false} // Disable event dragging
          eventResizableFromStart={false} // Disable event resizing
          eventDurationEditable={false} // Disable event duration editing
          displayEventTime={false}
          header={{
            left: "prev,next",
            center: "title",
            // right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          plugins={[dayGridPlugin, timeGridPlugin]}
          selectable={!isReadOnly}
          stickyHeaderDates
          selectAllow={(selectInfo) => {
            // Only allow selection if start and end are on the same day
            const startDate = selectInfo.start.toDateString();
            const endDate = selectInfo.end.toDateString();
            return startDate === endDate;
          }}
          select={handleSelectSlots}
          datesSet={onDatesSet}
          customButtons={{
            jumpToButton: {
              text: "Jump To",
              click: onPressJumpTo,
            },
          }}
          // eventDidMount={(info) => {
          //   // For finer control, you can modify the event DOM element directly
          //   if (info.view.type.includes("timeGrid")) {
          //     const eventEl = info.el;
          //     console.log("info.event", info.event.overlap);
          //     // Check if this is an overlapping event by looking at its container
          //     if (
          //       info.event.title === "EVENT_SLOT" &&
          //       eventEl.closest(".fc-timegrid-event-harness-inset")
          //     ) {
          //       eventEl.closest(".fc-timegrid-event-harness-inset").style.width = "75%";
          //       eventEl.closest(".fc-timegrid-event-harness-inset").style.left = "25%";
          //     }
          //   }
          // }}
        />
        <CircularLoader hasBackdrop={false} isLoading={isLoading} color="info" />
      </MDBox>
      <MonthSelectionMenu
        anchorEl={anchorEl}
        handleClose={() => setAnchorEl(null)}
        handleClick={handleClickMonth}
        startingDate={startingDate}
        currentDate={currentDateCalendar}
        pastMonths={0}
      />
    </>
  );
};

export default JobAvailabilityCalendar;
